import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Car,
  ChevronRight,
  Clock,
  DollarSign,
  Flame,
  Plus,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Layout } from "@/components/layout";
import {
  type Customer,
  type DashboardStats,
  formatCurrency,
  formatRelativeDate,
  getInitials,
  getSourceDisplayName,
  type Task,
} from "@/lib/types";

// Color palette for avatar backgrounds - Fresh design
const avatarColors = [
  "bg-indigo-100 text-indigo-700",
  "bg-violet-100 text-violet-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
];

function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
  return avatarColors[index];
}

export function Home() {
  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const res = await fetch("/api/dashboard");
      if (!res.ok) throw new Error("Failed to fetch dashboard");
      return res.json();
    },
  });

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Dashboard</h1>
            <p className="mt-1 text-slate-500">ยินดีต้อนรับ! นี่คือสรุปวันนี้</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl" asChild>
              <Link to="/tasks">
                <Clock className="mr-2 size-4" />
                ดูงาน
              </Link>
            </Button>
            <Button className="rounded-xl bg-indigo-500 hover:bg-indigo-600" asChild>
              <Link to="/customers?new=true">
                <Plus className="mr-2 size-4" />
                ลูกค้าใหม่
              </Link>
            </Button>
          </div>
        </div>

        {/* Total Sales - Highlighted Card */}
        <Card className="rounded-2xl border-emerald-200 bg-white shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">ยอดขายรวม</p>
                <p className="mt-2 text-4xl font-bold text-emerald-600">
                  {isLoading ? "..." : formatCurrency(stats?.totalSales ?? 0)}
                </p>
                <p className="mt-1 text-sm text-slate-500">จากดีลที่ปิดสำเร็จทั้งหมด</p>
              </div>
              <div className="flex size-14 items-center justify-center rounded-2xl bg-emerald-50">
                <TrendingUp className="size-7 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid - Fresh Design */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">ลูกค้าทั้งหมด</span>
                <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-50">
                  <Users className="size-5 text-indigo-600" />
                </div>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {isLoading ? "..." : (stats?.totalLeads ?? 0)}
              </p>
              <p className="mt-1 text-sm text-slate-500">ในระบบ</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">ลูกค้า Hot</span>
                <div className="flex size-10 items-center justify-center rounded-xl bg-rose-50">
                  <Flame className="size-5 text-rose-600" />
                </div>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {isLoading ? "..." : (stats?.hotLeads ?? 0)}
              </p>
              <p className="mt-1 text-sm text-rose-600">พร้อมปิดการขาย</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">มูลค่า Pipeline</span>
                <div className="flex size-10 items-center justify-center rounded-xl bg-amber-50">
                  <DollarSign className="size-5 text-amber-600" />
                </div>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {isLoading ? "..." : formatCurrency(stats?.pipelineValue ?? 0)}
              </p>
              <p className="mt-1 text-sm text-amber-600">กำลังดำเนินการ</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">งานวันนี้</span>
                <div className="flex size-10 items-center justify-center rounded-xl bg-violet-50">
                  <Calendar className="size-5 text-violet-600" />
                </div>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {isLoading ? "..." : (stats?.tasksDueToday ?? 0)}
              </p>
              <p className="mt-1 text-sm text-violet-600">ต้องทำวันนี้</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Leads */}
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-slate-900">ลูกค้าล่าสุด</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-indigo-600 hover:text-indigo-700"
                asChild
              >
                <Link to="/customers">
                  ดูทั้งหมด
                  <ChevronRight className="ml-1 size-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex animate-pulse items-center gap-3 rounded-xl bg-slate-50 p-3"
                    >
                      <div className="size-10 rounded-full bg-slate-100" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-24 rounded bg-slate-100" />
                        <div className="h-3 w-32 rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : stats?.recentLeads && stats.recentLeads.length > 0 ? (
                stats.recentLeads.map((customer: Customer) => (
                  <Link
                    key={customer.id}
                    to={`/customers/${customer.id}`}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3 transition-all hover:border-indigo-200 hover:bg-indigo-50/30"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="size-10">
                        <AvatarFallback className={getAvatarColor(customer.name)}>
                          {getInitials(customer.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{customer.name}</p>
                        <p className="text-xs text-slate-500">
                          {customer.vehicle_interest || "สอบถามทั่วไป"} •{" "}
                          {getSourceDisplayName(customer.source)}
                        </p>
                      </div>
                    </div>
                    <Badge variant={customer.status}>
                      {customer.status === "hot"
                        ? "Hot"
                        : customer.status === "warm"
                          ? "Warm"
                          : "Cold"}
                    </Badge>
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-slate-100">
                    <Users className="size-6 text-slate-400" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-slate-600">ยังไม่มีลูกค้า</p>
                  <p className="text-xs text-slate-400">เพิ่มลูกค้าคนแรกเพื่อเริ่มต้น</p>
                  <Button
                    className="mt-4 rounded-xl bg-indigo-500 hover:bg-indigo-600"
                    size="sm"
                    asChild
                  >
                    <Link to="/customers?new=true">
                      <Plus className="mr-2 size-4" />
                      เพิ่มลูกค้า
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Tasks */}
          <Card className="rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-semibold text-slate-900">
                งานที่กำลังจะถึง
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="text-indigo-600 hover:text-indigo-700"
                asChild
              >
                <Link to="/tasks">
                  ดูทั้งหมด
                  <ChevronRight className="ml-1 size-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex animate-pulse items-center gap-3 rounded-xl bg-slate-50 p-3"
                    >
                      <div className="size-8 rounded-lg bg-slate-100" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 rounded bg-slate-100" />
                        <div className="h-3 w-24 rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : stats?.upcomingTasks && stats.upcomingTasks.length > 0 ? (
                stats.upcomingTasks.map((task: Task) => (
                  <div
                    key={task.id}
                    className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex size-8 items-center justify-center rounded-lg ${
                          task.priority === "urgent"
                            ? "bg-red-50"
                            : task.priority === "normal"
                              ? "bg-indigo-50"
                              : "bg-slate-100"
                        }`}
                      >
                        <Clock
                          className={`size-4 ${
                            task.priority === "urgent"
                              ? "text-red-600"
                              : task.priority === "normal"
                                ? "text-indigo-600"
                                : "text-slate-500"
                          }`}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{task.title}</p>
                        <p className="text-xs text-slate-500">
                          {formatRelativeDate(task.due_date)}
                          {task.customer && ` • ${task.customer.name}`}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        task.priority === "urgent"
                          ? "priority-urgent"
                          : task.priority === "normal"
                            ? "priority-normal"
                            : "priority-low"
                      }
                    >
                      {task.priority === "urgent"
                        ? "ด่วน"
                        : task.priority === "normal"
                          ? "ปกติ"
                          : "ต่ำ"}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-slate-100">
                    <Clock className="size-6 text-slate-400" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-slate-600">ไม่มีงานที่กำลังจะถึง</p>
                  <p className="text-xs text-slate-400">คุณทำงานครบหมดแล้ว!</p>
                  <Button
                    className="mt-4 rounded-xl bg-indigo-500 hover:bg-indigo-600"
                    size="sm"
                    asChild
                  >
                    <Link to="/tasks?new=true">
                      <Plus className="mr-2 size-4" />
                      เพิ่มงาน
                    </Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="rounded-2xl border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-slate-900">ทางลัด</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Button
                variant="outline"
                className="h-auto flex-col gap-2 rounded-xl border-slate-200 py-4 hover:border-indigo-200 hover:bg-indigo-50/50"
                asChild
              >
                <Link to="/customers?new=true">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-indigo-50">
                    <Users className="size-5 text-indigo-600" />
                  </div>
                  <span className="text-slate-700">เพิ่มลูกค้าใหม่</span>
                </Link>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col gap-2 rounded-xl border-slate-200 py-4 hover:border-emerald-200 hover:bg-emerald-50/50"
                asChild
              >
                <Link to="/inventory?new=true">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-emerald-50">
                    <Car className="size-5 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">เพิ่มรถยนต์</span>
                </Link>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col gap-2 rounded-xl border-slate-200 py-4 hover:border-violet-200 hover:bg-violet-50/50"
                asChild
              >
                <Link to="/tasks?new=true">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-violet-50">
                    <Calendar className="size-5 text-violet-600" />
                  </div>
                  <span className="text-slate-700">นัดติดตาม</span>
                </Link>
              </Button>
              <Button
                variant="outline"
                className="h-auto flex-col gap-2 rounded-xl border-slate-200 py-4 hover:border-amber-200 hover:bg-amber-50/50"
                asChild
              >
                <Link to="/pipeline">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-amber-50">
                    <TrendingUp className="size-5 text-amber-600" />
                  </div>
                  <span className="text-slate-700">ดู Pipeline</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
